import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  AppstoreOutlined,
  DeleteOutlined,
  DragOutlined,
} from '@ant-design/icons';

const { Paragraph, Text, Title } = Typography;

const DROP_MIME = 'application/x-power-move-component';

const VIEW_ANGLE_LABELS = {
  front: '正面',
  side: '侧面',
  oblique: '斜侧面',
};

const JOINT_OPTIONS = [
  { value: 'knee', label: '膝关节' },
  { value: 'hip', label: '髋关节' },
  { value: 'elbow', label: '肘关节' },
  { value: 'shoulder', label: '肩关节' },
];

const SIDE_POLICY_OPTIONS = [
  { value: 'visible_side', label: '自动选择可见侧' },
  { value: 'left', label: '左侧' },
  { value: 'right', label: '右侧' },
];

const SLOT_META = {
  mainSignal: { label: '主分析信号', role: 'primary_signal' },
  amplitudeSignals: { label: '活动范围信号', role: 'primary_range' },
  postureSignals: { label: '姿态信号', role: 'posture' },
  stabilitySignals: { label: '稳定性信号', role: 'stability' },
};

function parseConfig(value) {
  if (!value) return null;
  try {
    const result = typeof value === 'string' ? JSON.parse(value) : value;
    return result && typeof result === 'object' ? result : null;
  } catch (error) {
    return null;
  }
}

function defaultStrategy(operator, mainSignal) {
  const signal = mainSignal || 'knee_angle';
  if (operator === 'HoldDuration') {
    return {
      type: operator,
      mainSignal: signal,
      targetValue: 160,
      tolerance: 10,
      graceMs: 500,
      holdMs: 30000,
    };
  }
  if (operator === 'StretchHold') {
    return {
      type: operator,
      mainSignal: signal,
      target: { threshold: 90, operator: '<=' },
      tolerance: 10,
      graceMs: 500,
      holdMs: 3000,
    };
  }
  if (operator === 'PositionSwitch') {
    return {
      type: operator,
      mainSignal: signal,
      conditionA: {
        require: [{ signal, operator: '>=', threshold: 0 }],
      },
      conditionB: {
        require: [{ signal, operator: '<=', threshold: 0 }],
      },
    };
  }
  return {
    type: 'AngleCycle',
    mainSignal: signal,
    keyframes: [
      { name: 'top', rule: 'max' },
      { name: 'bottom', rule: 'min' },
    ],
    stages: [
      { name: 'down', stageType: 'eccentric', from: 'top', to: 'bottom' },
      { name: 'up', stageType: 'concentric', from: 'bottom', to: 'top' },
    ],
  };
}

function createConfig(actionId, actionName) {
  return {
    actionId: String(actionId),
    actionName: actionName || `动作 ${actionId}`,
    actionType: 'repetition',
    resultType: 'count',
    viewAngle: 'side',
    evaluable: true,
    strategy: defaultStrategy('AngleCycle', 'knee_angle'),
    traits: [],
    targetPlan: {
      primaryJoints: [
        { joint: 'knee', sidePolicy: 'visible_side', role: 'primary_range' },
      ],
      amplitudeSignals: [],
      postureSignals: [],
      stabilitySignals: [],
    },
    compilePolicy: {
      enableBilateralFeatures: false,
      enablePowerFeatures: false,
    },
  };
}

function normalizeConfig(value, actionId, actionName) {
  const parsed = parseConfig(value);
  const base = parsed || createConfig(actionId, actionName);
  return {
    ...base,
    actionId: String(actionId),
    actionName: base.actionName || actionName || `动作 ${actionId}`,
    strategy: base.strategy || defaultStrategy('AngleCycle'),
    traits: Array.isArray(base.traits) ? base.traits : [],
    targetPlan: {
      primaryJoints: [],
      amplitudeSignals: [],
      postureSignals: [],
      stabilitySignals: [],
      ...(base.targetPlan || {}),
    },
    compilePolicy: {
      enableBilateralFeatures: false,
      enablePowerFeatures: false,
      ...(base.compilePolicy || {}),
    },
  };
}

function componentLabel(component) {
  return component?.label || component?.code || '未选择';
}

function DraggableBlock({ component, color }) {
  return (
    <div
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'copy';
        event.dataTransfer.setData(DROP_MIME, JSON.stringify(component));
      }}
      style={{
        padding: '9px 10px',
        marginBottom: 8,
        border: `1px solid ${color}`,
        borderRadius: 8,
        background: '#fff',
        cursor: 'grab',
      }}
      title={component.description}
    >
      <Space size={6}>
        <DragOutlined style={{ color }} />
        <Text>{componentLabel(component)}</Text>
      </Space>
      <div>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {component.code}
        </Text>
      </div>
    </div>
  );
}

DraggableBlock.propTypes = {
  component: PropTypes.object.isRequired,
  color: PropTypes.string.isRequired,
};

function DropSlot({ title, hint, children, onDrop }) {
  const [active, setActive] = useState(false);
  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        setActive(true);
      }}
      onDragLeave={() => setActive(false)}
      onDrop={(event) => {
        event.preventDefault();
        setActive(false);
        try {
          onDrop(JSON.parse(event.dataTransfer.getData(DROP_MIME)));
        } catch (error) {
          // 非本工作台拖拽内容不产生任何配置变更。
        }
      }}
      style={{
        minHeight: 78,
        padding: 12,
        border: `1px dashed ${active ? '#1890ff' : '#b8c7d9'}`,
        borderRadius: 9,
        background: active ? '#e6f7ff' : '#f8fafc',
        transition: 'all .15s ease',
      }}
    >
      <Text strong>{title}</Text>
      <div style={{ marginTop: 8 }}>
        {children || <Text type="secondary">{hint}</Text>}
      </div>
    </div>
  );
}

DropSlot.propTypes = {
  title: PropTypes.string.isRequired,
  hint: PropTypes.string,
  children: PropTypes.node,
  onDrop: PropTypes.func.isRequired,
};

DropSlot.defaultProps = {
  hint: '拖拽组件到这里',
  children: null,
};

export default function MotionConfigVisualBuilder({
  actionId,
  actionName,
  value,
  context,
  onChange,
}) {
  const [config, setConfig] = useState(() =>
    normalizeConfig(value, actionId, actionName),
  );
  const [signalKeyword, setSignalKeyword] = useState('');
  const sourceInvalid = !!value && !parseConfig(value);

  useEffect(() => {
    const next = normalizeConfig(value, actionId, actionName);
    setConfig(next);
    if (!value) {
      onChange(JSON.stringify(next, null, 2));
    }
  }, [actionId, actionName, onChange, value]);

  const componentsByCode = useMemo(() => {
    const result = {};
    [
      'operators',
      'signals',
      'features',
      'traits',
      'capabilityDimensions',
    ].forEach((group) => {
      (context?.[group] || []).forEach((item) => {
        result[item.code] = item;
      });
    });
    return result;
  }, [context]);

  const derivedFeatures = useMemo(
    () =>
      (context?.features || []).filter((feature) =>
        config.traits.includes(feature.properties?.trait),
      ),
    [config.traits, context],
  );

  const visibleSignals = useMemo(() => {
    const keyword = signalKeyword.trim().toLowerCase();
    if (!keyword) return context?.signals || [];
    return (context?.signals || []).filter(
      (signal) =>
        signal.code.toLowerCase().includes(keyword) ||
        (signal.label || '').toLowerCase().includes(keyword),
    );
  }, [context, signalKeyword]);

  const commit = (next) => {
    setConfig(next);
    onChange(JSON.stringify(next, null, 2));
  };

  const patch = (partial) => commit({ ...config, ...partial });

  const chooseOperator = (component) => {
    if (component?.category !== 'operator') return;
    const actionType = component.properties?.actionType || config.actionType;
    patch({
      actionType,
      resultType:
        context?.actionTypeResultTypes?.[actionType] ||
        (actionType === 'hold' ? 'duration' : 'count'),
      strategy: defaultStrategy(component.code, config.strategy?.mainSignal),
    });
  };

  const chooseMainSignal = (component) => {
    if (component?.category !== 'signal') return;
    const strategy = { ...config.strategy, mainSignal: component.code };
    if (strategy.type === 'PositionSwitch') {
      strategy.conditionA = {
        require: (strategy.conditionA?.require || []).map((item) => ({
          ...item,
          signal: component.code,
        })),
      };
      strategy.conditionB = {
        require: (strategy.conditionB?.require || []).map((item) => ({
          ...item,
          signal: component.code,
        })),
      };
    }
    patch({ strategy });
  };

  const addSignal = (slot, component) => {
    if (component?.category !== 'signal') return;
    const current = config.targetPlan[slot] || [];
    if (current.some((item) => item.signal === component.code)) return;
    patch({
      targetPlan: {
        ...config.targetPlan,
        [slot]: [
          ...current,
          { signal: component.code, role: SLOT_META[slot].role },
        ],
      },
    });
  };

  const removeSignal = (slot, signal) => {
    patch({
      targetPlan: {
        ...config.targetPlan,
        [slot]: (config.targetPlan[slot] || []).filter(
          (item) => item.signal !== signal,
        ),
      },
    });
  };

  const addTrait = (component) => {
    if (
      component?.category !== 'trait' ||
      config.traits.includes(component.code)
    )
      return;
    const compilePolicy = { ...config.compilePolicy };
    if (component.code === 'coordination') {
      compilePolicy.enableBilateralFeatures = true;
    }
    if (component.code === 'agility' || component.code === 'powerPerformance') {
      compilePolicy.enablePowerFeatures = true;
    }
    patch({
      traits: [...config.traits, component.code],
      compilePolicy,
    });
  };

  const removeTrait = (trait) =>
    patch({ traits: config.traits.filter((item) => item !== trait) });

  const primaryJoint = config.targetPlan.primaryJoints?.[0] || {
    joint: 'knee',
    sidePolicy: 'visible_side',
    role: 'primary_range',
  };

  const updatePrimaryJoint = (partial) =>
    patch({
      targetPlan: {
        ...config.targetPlan,
        primaryJoints: [{ ...primaryJoint, ...partial }],
      },
    });

  if (!context) {
    return (
      <Alert
        type="warning"
        showIcon
        message="组件注册表尚未加载，暂时使用高级 JSON 编辑"
      />
    );
  }

  if (sourceInvalid) {
    return (
      <Alert
        type="error"
        showIcon
        message="当前草稿不是合法 JSON，请先在高级 JSON 模式修复"
      />
    );
  }

  const operator = componentsByCode[config.strategy?.type];
  const mainSignal = componentsByCode[config.strategy?.mainSignal];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message={`组件上下文 ${context.contextVersion}`}
        description="拖拽只允许使用端云 Core 共同支持的组件；Layer 3 特征由信号、算子与 Layer 4 特征组自动推导。"
      />
      <Row gutter={16} align="top">
        <Col span={6}>
          <Card
            size="small"
            title={
              <Space>
                <AppstoreOutlined />
                可用组件
              </Space>
            }
          >
            <Title level={5}>算子</Title>
            {(context.operators || []).map((item) => (
              <DraggableBlock
                key={item.code}
                component={item}
                color="#722ed1"
              />
            ))}
            <Title level={5} style={{ marginTop: 16 }}>
              物理信号
            </Title>
            <Input.Search
              allowClear
              value={signalKeyword}
              placeholder="搜索信号名称或编码"
              onChange={(event) => setSignalKeyword(event.target.value)}
              style={{ marginBottom: 10 }}
            />
            <div style={{ maxHeight: 360, overflow: 'auto', paddingRight: 4 }}>
              {visibleSignals.map((item) => (
                <DraggableBlock
                  key={item.code}
                  component={item}
                  color="#fa8c16"
                />
              ))}
            </div>
            <Title level={5} style={{ marginTop: 16 }}>
              Layer 4 特征组
            </Title>
            {(context.traits || []).map((item) => (
              <DraggableBlock
                key={item.code}
                component={item}
                color="#13a8a8"
              />
            ))}
          </Card>
        </Col>

        <Col span={12}>
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Card size="small" title="① 动作基础">
              <Form layout="vertical">
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item label="动作名称" style={{ marginBottom: 8 }}>
                      <Input
                        value={config.actionName}
                        onChange={(event) =>
                          patch({ actionName: event.target.value })
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="最佳观测视角" style={{ marginBottom: 8 }}>
                      <Select
                        value={config.viewAngle}
                        onChange={(viewAngle) => patch({ viewAngle })}
                        options={(context.viewAngles || []).map((item) => ({
                          value: item,
                          label: VIEW_ANGLE_LABELS[item] || item,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>

            <Card size="small" title="② 分析算子">
              <DropSlot title="主算子" onDrop={chooseOperator}>
                {operator && (
                  <Tag color="purple" closable={false}>
                    {componentLabel(operator)}
                  </Tag>
                )}
              </DropSlot>
            </Card>

            <Card size="small" title="③ 物理信号">
              <Space direction="vertical" size={10} style={{ width: '100%' }}>
                <DropSlot title="主分析信号" onDrop={chooseMainSignal}>
                  {mainSignal && (
                    <Tag color="orange">{componentLabel(mainSignal)}</Tag>
                  )}
                </DropSlot>
                {Object.keys(SLOT_META)
                  .filter((slot) => slot !== 'mainSignal')
                  .map((slot) => (
                    <DropSlot
                      key={slot}
                      title={SLOT_META[slot].label}
                      onDrop={(component) => addSignal(slot, component)}
                    >
                      <Space wrap>
                        {(config.targetPlan[slot] || []).map((item) => (
                          <Tag
                            key={item.signal}
                            closable
                            color="orange"
                            onClose={() => removeSignal(slot, item.signal)}
                          >
                            {componentLabel(
                              componentsByCode[item.signal] || {
                                code: item.signal,
                              },
                            )}
                          </Tag>
                        ))}
                      </Space>
                    </DropSlot>
                  ))}
              </Space>
            </Card>

            <Card size="small" title="④ Layer 4 特征组织">
              <DropSlot title="客观特征组" onDrop={addTrait}>
                <Space wrap>
                  {config.traits.map((trait) => (
                    <Tag
                      key={trait}
                      closable
                      color="cyan"
                      onClose={() => removeTrait(trait)}
                    >
                      {componentLabel(
                        componentsByCode[trait] || { code: trait },
                      )}
                    </Tag>
                  ))}
                </Space>
              </DropSlot>
            </Card>

            <Card size="small" title="⑤ 自动推导的 Layer 3 特征">
              {derivedFeatures.length ? (
                <Space wrap>
                  {derivedFeatures.map((feature) => (
                    <Tag key={feature.code} color="green">
                      {componentLabel(feature)}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="选择 Layer 4 特征组后展示"
                />
              )}
            </Card>
          </Space>
        </Col>

        <Col span={6}>
          <Card size="small" title="参数面板">
            <Form layout="vertical">
              <Form.Item label="动作类型">
                <Select
                  value={config.actionType}
                  disabled
                  options={Object.keys(context.actionTypeResultTypes || {}).map(
                    (item) => ({ value: item, label: item }),
                  )}
                />
              </Form.Item>
              <Form.Item label="结果类型">
                <Input value={config.resultType} disabled />
              </Form.Item>
              <Form.Item label="主要关节">
                <Select
                  value={primaryJoint.joint}
                  options={JOINT_OPTIONS}
                  onChange={(joint) => updatePrimaryJoint({ joint })}
                />
              </Form.Item>
              <Form.Item label="侧别策略">
                <Select
                  value={primaryJoint.sidePolicy}
                  options={SIDE_POLICY_OPTIONS}
                  onChange={(sidePolicy) => updatePrimaryJoint({ sidePolicy })}
                />
              </Form.Item>
              <Form.Item>
                <Checkbox
                  checked={config.evaluable !== false}
                  onChange={(event) =>
                    patch({ evaluable: event.target.checked })
                  }
                >
                  可分析
                </Checkbox>
              </Form.Item>
              {config.strategy?.type === 'HoldDuration' && (
                <>
                  <Form.Item label="目标值">
                    <InputNumber
                      style={{ width: '100%' }}
                      value={config.strategy.targetValue}
                      onChange={(targetValue) =>
                        patch({ strategy: { ...config.strategy, targetValue } })
                      }
                    />
                  </Form.Item>
                  <Form.Item label="目标保持时长（ms）">
                    <InputNumber
                      min={1}
                      style={{ width: '100%' }}
                      value={config.strategy.holdMs}
                      onChange={(holdMs) =>
                        patch({ strategy: { ...config.strategy, holdMs } })
                      }
                    />
                  </Form.Item>
                </>
              )}
              {config.strategy?.type === 'StretchHold' && (
                <>
                  <Form.Item label="目标阈值">
                    <InputNumber
                      style={{ width: '100%' }}
                      value={config.strategy.target?.threshold}
                      onChange={(threshold) =>
                        patch({
                          strategy: {
                            ...config.strategy,
                            target: { ...config.strategy.target, threshold },
                          },
                        })
                      }
                    />
                  </Form.Item>
                  <Form.Item label="比较关系">
                    <Select
                      value={config.strategy.target?.operator}
                      options={[
                        { value: '<=', label: '≤' },
                        { value: '>=', label: '≥' },
                      ]}
                      onChange={(comparison) =>
                        patch({
                          strategy: {
                            ...config.strategy,
                            target: {
                              ...config.strategy.target,
                              operator: comparison,
                            },
                          },
                        })
                      }
                    />
                  </Form.Item>
                </>
              )}
              {config.strategy?.type === 'PositionSwitch' && (
                <>
                  {['conditionA', 'conditionB'].map((conditionKey) => {
                    const condition = config.strategy[conditionKey]
                      ?.require?.[0] || {
                      signal: config.strategy.mainSignal,
                      operator: '>=',
                      threshold: 0,
                    };
                    const updateCondition = (partial) =>
                      patch({
                        strategy: {
                          ...config.strategy,
                          [conditionKey]: {
                            require: [{ ...condition, ...partial }],
                          },
                        },
                      });
                    return (
                      <Card
                        key={conditionKey}
                        size="small"
                        title={
                          conditionKey === 'conditionA' ? '状态 A' : '状态 B'
                        }
                        style={{ marginBottom: 12 }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Select
                            value={condition.operator}
                            options={['>', '>=', '<', '<=', '=='].map(
                              (item) => ({
                                value: item,
                                label: item,
                              }),
                            )}
                            onChange={(operatorValue) =>
                              updateCondition({ operator: operatorValue })
                            }
                          />
                          <InputNumber
                            style={{ width: '100%' }}
                            value={condition.threshold}
                            onChange={(threshold) =>
                              updateCondition({ threshold })
                            }
                          />
                        </Space>
                      </Card>
                    );
                  })}
                </>
              )}
              <Form.Item>
                <Checkbox
                  checked={!!config.compilePolicy.enableBilateralFeatures}
                  onChange={(event) =>
                    patch({
                      compilePolicy: {
                        ...config.compilePolicy,
                        enableBilateralFeatures: event.target.checked,
                      },
                    })
                  }
                >
                  启用双侧特征
                </Checkbox>
              </Form.Item>
              <Form.Item>
                <Checkbox
                  checked={!!config.compilePolicy.enablePowerFeatures}
                  onChange={(event) =>
                    patch({
                      compilePolicy: {
                        ...config.compilePolicy,
                        enablePowerFeatures: event.target.checked,
                      },
                    })
                  }
                >
                  启用速度与加速度特征
                </Checkbox>
              </Form.Item>
              <Button
                danger
                block
                icon={<DeleteOutlined />}
                onClick={() => commit(createConfig(actionId, actionName))}
              >
                重置编排
              </Button>
            </Form>
          </Card>
          <Paragraph type="secondary" style={{ marginTop: 12, fontSize: 12 }}>
            当前只组织客观计算链路，不在配置中产生好坏判断或问题结论。
          </Paragraph>
        </Col>
      </Row>
    </Space>
  );
}

MotionConfigVisualBuilder.propTypes = {
  actionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  actionName: PropTypes.string,
  value: PropTypes.string,
  context: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

MotionConfigVisualBuilder.defaultProps = {
  actionName: '',
  value: '',
  context: null,
};
